import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { FontSize, LineHeight, Hyperlink } from './tiptapExtensions';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/database';

const MenuBar = ({ editor, sectionType, enableLinks }) => {
  const [showSnippets, setShowSnippets] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customHex, setCustomHex] = useState('');

  const allSnippets = useLiveQuery(() => db.snippets.toArray()) || [];
  const colorPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryMap = {
    inScope: ['Standard', 'Technical Specs'],
    exclusions: ['Exclusions'],
    financials: ['Pricing'],
    changeControl: ['Legal Clauses'],
    deliverables: ['Technical Specs'],
    slas: ['SLAs', 'Support'],
    revisionLimits: ['Revisions'],
    terms: ['Legal Clauses', 'Pricing']
  };

  const targetCategories = categoryMap[sectionType] || [];

  const filteredSnippets = useMemo(() => {
    let list = allSnippets;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return list.filter(s => 
        s.title.toLowerCase().includes(term) || 
        s.category.toLowerCase().includes(term)
      );
    }

    // Sort to prioritize context-relevant snippets, but keep full library accessible
    return [...list].sort((a, b) => {
      if (targetCategories.length > 0) {
        const aMatch = targetCategories.includes(a.category) ? 1 : 0;
        const bMatch = targetCategories.includes(b.category) ? 1 : 0;
        if (aMatch !== bMatch) {
          return bMatch - aMatch; // Relevant items first
        }
      }
      return a.title.localeCompare(b.title); // Alphabetical fallback
    });
  }, [allSnippets, searchTerm, targetCategories]);

  if (!editor) return null;

  const insertSnippet = (content) => {
    editor.chain().focus().insertContent(content).run();
    setShowSnippets(false);
    setSearchTerm('');
  };

  const applyColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setCustomHex(color);
  };

  const btnClass = (isActive) =>
    `p-1.5 rounded hover:bg-gray-200 transition-colors ${isActive ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600'}`;

  const Divider = () => <div className="w-px h-5 bg-gray-300 mx-1"></div>;

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-gray-200 bg-gray-50 rounded-t-lg relative">
      {/* Basic Styles */}
      <div className="flex items-center">
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))} title="Bold">
          <SafeIcon icon={FiIcons.FiBold} />
        </button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))} title="Italic">
          <SafeIcon icon={FiIcons.FiItalic} />
        </button>
      </div>
      <Divider />

      {/* Alignment */}
      <div className="flex items-center">
        <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={btnClass(editor.isActive({ textAlign: 'left' }))} title="Align Left">
          <SafeIcon icon={FiIcons.FiAlignLeft} />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={btnClass(editor.isActive({ textAlign: 'center' }))} title="Align Center">
          <SafeIcon icon={FiIcons.FiAlignCenter} />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={btnClass(editor.isActive({ textAlign: 'right' }))} title="Align Right">
          <SafeIcon icon={FiIcons.FiAlignRight} />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign('justify').run()} className={btnClass(editor.isActive({ textAlign: 'justify' }))} title="Justify">
          <SafeIcon icon={FiIcons.FiAlignJustify} />
        </button>
      </div>
      <Divider />

      {/* Lists */}
      <div className="flex items-center">
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))} title="Bullet List">
          <SafeIcon icon={FiIcons.FiList} />
        </button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))} title="Ordered List">
          <SafeIcon icon={FiIcons.FiHash} />
        </button>
      </div>
      <Divider />

      {/* Typography Dropdowns */}
      <div className="flex items-center gap-1">
        <select
          onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
          className="text-[10px] font-bold border border-gray-300 rounded px-1 py-1 bg-white focus:ring-1 focus:ring-indigo-500 outline-none w-16"
          title="Font Size"
        >
          <option value="">Size</option>
          <option value="10pt">10pt</option>
          <option value="11pt">11pt</option>
          <option value="12pt">12pt</option>
          <option value="14pt">14pt</option>
          <option value="16pt">16pt</option>
        </select>
        <select
          onChange={(e) => editor.chain().focus().setLineHeight(e.target.value).run()}
          className="text-[10px] font-bold border border-gray-300 rounded px-1 py-1 bg-white focus:ring-1 focus:ring-indigo-500 outline-none w-16"
          title="Line Spacing"
        >
          <option value="">Space</option>
          <option value="1">1.0</option>
          <option value="1.15">1.15</option>
          <option value="1.3">1.3</option>
          <option value="1.5">1.5</option>
          <option value="1.75">1.75</option>
          <option value="2">2.0</option>
        </select>
      </div>
      <Divider />

      {/* Hyperlinks (opt-in for surgical use in Global Terms) */}
      {enableLinks && (
        <>
          <div className="relative">
            <button
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (!editor.state.selection || editor.state.selection.empty) return;
                const currentHref = editor.getAttributes('hyperlink').href || '';
                const entered = window.prompt(currentHref ? 'Edit hyperlink URL' : 'Enter hyperlink URL', currentHref);
                if (entered === null) return;
                if (!entered.trim()) {
                  editor.chain().focus().unsetHyperlink().run();
                  return;
                }
                if (!editor.chain().focus().setHyperlink(entered).run()) {
                  window.alert('Please enter a valid http://, https://, mailto:, or domain URL.');
                }
              }}
              disabled={editor.state.selection.empty}
              className={btnClass(editor.isActive('hyperlink')) + ' disabled:opacity-40 disabled:cursor-not-allowed'}
              title="Add or edit hyperlink"
            >
              <SafeIcon icon={FiIcons.FiLink} />
            </button>
          </div>
          <Divider />
        </>
      )}

      {/* Color Picker */}
      <div className="relative" ref={colorPickerRef}>
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className={btnClass(showColorPicker)}
          title="Text Color"
        >
          <div className="flex flex-col items-center">
            <SafeIcon icon={FiIcons.FiDroplet} />
            <div className="w-3 h-0.5 mt-0.5" style={{ backgroundColor: editor.getAttributes('textStyle').color || '#000000' }}></div>
          </div>
        </button>
        {showColorPicker && (
          <div className="absolute left-0 top-full mt-1 p-3 bg-white border border-gray-200 shadow-xl rounded-lg z-[110] min-w-[160px]">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="color"
                value={editor.getAttributes('textStyle').color || '#000000'}
                onChange={(e) => applyColor(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border-none p-0"
              />
              <input
                type="text"
                placeholder="#000000"
                value={customHex}
                onChange={(e) => applyColor(e.target.value)}
                className="flex-1 text-[10px] font-mono border border-gray-200 rounded px-2 py-1 uppercase"
              />
            </div>
            <button
              onClick={() => { editor.chain().focus().unsetColor().run(); setShowColorPicker(false); }}
              className="w-full py-1.5 text-[10px] font-bold text-gray-500 hover:text-gray-900 border border-gray-100 rounded bg-gray-50 hover:bg-gray-100 transition-colors uppercase tracking-wider"
            >
              Reset Color
            </button>
          </div>
        )}
      </div>

      <div className="flex-grow"></div>

      {/* Snippets Button */}
      <div className="relative">
        <button
          onClick={() => setShowSnippets(!showSnippets)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-all uppercase tracking-wider shadow-sm"
        >
          <SafeIcon icon={FiIcons.FiPlus} /> Snippets
        </button>

        {showSnippets && (
          <div className="absolute right-0 top-full mt-2 w-[360px] bg-white border border-gray-200 shadow-2xl rounded-xl z-[9999] overflow-hidden">
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <div className="relative">
                <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search library..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                />
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto py-1">
              {filteredSnippets.length === 0 ? (
                <div className="p-4 text-xs text-gray-400 text-center italic">No matching snippets</div>
              ) : (
                filteredSnippets.map(snippet => {
                  const isRecommended = !searchTerm && targetCategories.includes(snippet.category);
                  return (
                    <button
                      key={snippet.id}
                      onClick={() => insertSnippet(snippet.content)}
                      className="w-full text-left px-4 py-3 hover:bg-indigo-50 group transition-colors border-b border-gray-50 last:border-0"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div className="font-bold text-gray-900 group-hover:text-indigo-700 text-xs truncate pr-2">{snippet.title}</div>
                        {isRecommended && <span className="text-[8px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider whitespace-nowrap">Recommended</span>}
                      </div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-tighter">{snippet.category}</div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function RichTextEditor({ value, onChange, sectionType, enableLinks = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontSize,
      LineHeight,
      ...(enableLinks ? [Hyperlink] : []),
    ],
    content: value,
    onCreate: ({ editor }) => {
      if (enableLinks) {
        // Re-parse once so the hyperlink plugin can convert any existing plain URLs
        // (including the existing Global Terms URL) into real link marks.
        editor.commands.setContent(editor.getHTML());
      }
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none max-w-none p-4 min-h-[180px] cursor-text',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div className="border border-gray-200 rounded-xl overflow-visible bg-white shadow-sm hover:border-gray-300 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all">
      <MenuBar editor={editor} sectionType={sectionType} enableLinks={enableLinks} />
      <EditorContent editor={editor} />
    </div>
  );
}